import { Box, Paper, Tooltip } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { Button, ContainerPage } from "components";
import { ButtonLink } from "components/ButtonLink";
import { usePopup } from "hooks/usePopup";
import fileDownload from "js-file-download";
import { useEffect, useState } from "react";
import { BsKanban } from "react-icons/bs";
import { HiOutlineRefresh } from "react-icons/hi";
import batchService from "services/batchService";
import batchServiceV3 from "services/batchServiceV3";
import reporterGenerateService from "services/reporterGenerateService";
import storageService from "services/storageService";
import { useDebaunce } from "utils";

import { DashBatchTable } from "./components/DashBatchTable";
import { HorDashCards } from "./components/DashCards/HorCards";
import { VertDashCards } from "./components/DashCards/VertCards";
import { StorageTable } from "./components/StorageTable";
import {
  ButtonReportContent,
  ConfirmPendingAlert,
  ConfirmPendingAlertCount,
  ConfirmPendingAlertIconBox,
  ConfirmPendingAlertInfo,
  ConfirmPendingAlertLabel,
  Content,
  CountBadge,
  CustomTableContainer,
  DashboardHeader,
  DashboardPanel,
  DashboardTitle,
  MainContent,
  SearchContent,
  SearchInput,
  SectionTitle,
  TablePanelHeader,
  useStyles
} from "./styles";
import { formatData } from "./utils/formatData";

export const Dashboard = () => {
  const { addPopup } = usePopup();
  const [loadingStock, setLoadingStock] = useState(false);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [storageOutOfStock, setStorageOutOfStock] = useState([]);
  const [currentStorageOutOfStock, setCurrentStorageOutOfStock] = useState([]);
  const [batchData, setBatchData] = useState({});
  const [reportLoading, setReportLoading] = useState(false);
  const [batchReportLoading, setBatchReportLoading] = useState(false);
  const [confirmPendingData, setConfirmPendingData] = useState({});
  const [loadingConfirmPending, setLoadingConfirmPending] = useState(false);
  const classes = useStyles();

  const debaunceBatchSearch = useDebaunce({
    fn: handleBatchSearch,
    delay: 800
  });

  const debaunceStorageSearch = useDebaunce({
    fn: handleStorageSearch,
    delay: 800
  });

  const getStorageData = async search => {
    try {
      setLoadingStock(true);
      const currentParams = {
        outOfStockOnly: true,
        scheduledDebitMode: "none",
        search
      };
      const futureParams = {
        scheduledDebitMode: "all",
        search
      };
      const [currentStorageResponse, futureStorageResponse] = await Promise.all(
        [
          storageService.getStorageResume(currentParams),
          storageService.getStorageResume(futureParams)
        ]
      );
      const currentStorageData = currentStorageResponse.data.results;
      const futureStorageData = futureStorageResponse.data.results;

      setCurrentStorageOutOfStock(
        formatData.formatStorageData(currentStorageData)
      );
      setStorageOutOfStock(
        formatData.formatMergedStorageData(
          currentStorageData,
          futureStorageData
        )
      );
    } catch (error) {
      const errorMessage = error?.response?.data?.msg;
      addPopup({
        type: "error",
        title: "Erro ao carregar produtos",
        description: errorMessage
      });
    } finally {
      setLoadingStock(false);
    }
  };

  const getConfirmPendingData = async () => {
    try {
      setLoadingConfirmPending(true);
      const { data } = await batchService.getBatches({
        status: 2,
        pageSize: 1000
      });
      setConfirmPendingData(formatData.formatConfirmPendingData(data.results));
    } catch (error) {
      const errorMessage = error?.response?.data?.msg;
      addPopup({
        type: "error",
        title: "Erro ao carregar lotes pendentes",
        description: errorMessage
      });
    } finally {
      setLoadingConfirmPending(false);
    }
  };

  const getBatchData = async search => {
    try {
      setLoadingBatch(true);
      setBatchData([]);
      const params = {
        search
      };
      const { data } = await batchServiceV3.getBatchDash(params);

      setBatchData(formatData.formatBatchData(data));
    } catch (error) {
      const errorMessage = error?.response?.data?.msg;
      addPopup({
        type: "error",
        title: "Erro ao carregar lotes",
        description: errorMessage
      });
    } finally {
      setLoadingBatch(false);
    }
  };

  function handleStorageSearch(e) {
    const { value } = e.target;
    getStorageData(value?.trim());
  }

  function handleBatchSearch(e) {
    const { value } = e.target;
    getBatchData(value?.trim());
  }

  const handleDownloadOutOfStock = async () => {
    try {
      setReportLoading(true);
      const dataToReport = storageOutOfStock.map(storage => {
        return {
          ID: storage.product_base_id,
          Produto: storage.product_base_name,
          "Saldo atual": storage.currentQuantity,
          "Saldo futuro": storage.futureQuantity
        };
      });
      const { data } = await reporterGenerateService.excelGenerate(
        dataToReport
      );

      const currentDate = new Date();
      const formattedDate = currentDate
        .toLocaleString()
        .replace(/[:.,\s/]/g, "_");
      const fileName = `fora_de_estoque_${formattedDate}.xlsx`;

      fileDownload(data, fileName);
    } catch (err) {
      if (err?.response?.status !== 401) {
        addPopup({
          type: "error",
          description: "Erro ao fazer download"
        });
      }
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadDueBatches = async () => {
    try {
      setBatchReportLoading(true);
      const dataToReport = (batchData.batches || []).map(batch => ({
        Lote: batch.id,
        Produto: batch.product?.name,
        Classe: batch.classification,
        "Shelf life (dias)": batch.shelfLife,
        "Qtd. disponível": batch.available_quantity,
        Unidade: { 1: "Kg", 2: "Un" }[batch.purchase_type] || "",
        Vencimento: batch.due_date
      }));
      const { data } = await reporterGenerateService.excelGenerate(
        dataToReport
      );

      const formattedDate = new Date()
        .toLocaleString()
        .replace(/[:.,\s/]/g, "_");
      const fileName = `perto_do_vencimento_${formattedDate}.xlsx`;

      fileDownload(data, fileName);
    } catch (err) {
      if (err?.response?.status !== 401) {
        addPopup({
          type: "error",
          description: "Erro ao fazer download"
        });
      }
    } finally {
      setBatchReportLoading(false);
    }
  };

  useEffect(() => {
    getStorageData();
    getBatchData();
    getConfirmPendingData();
  }, []);

  return (
    <ContainerPage>
      <DashboardPanel>
        <DashboardHeader>
          <DashboardTitle variant="h1">Dashboard</DashboardTitle>
          <ButtonLink className={classes.button} to="/app/dashboard/kanban">
            <BsKanban size={16} />
            Acessar quadro
          </ButtonLink>
        </DashboardHeader>

        {confirmPendingData.unconfirmed > 0 && (
          <ConfirmPendingAlert>
            <ConfirmPendingAlertIconBox>
              <HiOutlineRefresh size={20} color="#FB8C00" />
            </ConfirmPendingAlertIconBox>
            <ConfirmPendingAlertInfo>
              <ConfirmPendingAlertLabel>
                Pendentes de confirmação
              </ConfirmPendingAlertLabel>
              <ConfirmPendingAlertCount>
                {confirmPendingData.unconfirmed} lotes
              </ConfirmPendingAlertCount>
            </ConfirmPendingAlertInfo>
          </ConfirmPendingAlert>
        )}

        <MainContent>
          <HorDashCards
            batchData={batchData}
            storageData={currentStorageOutOfStock}
            batchLoading={loadingBatch}
            stockLoading={loadingStock}
          />

          <Content gridArea="storage">
            <CustomTableContainer component={Paper}>
              <TablePanelHeader>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ gap: "0.5rem" }}
                >
                  <SectionTitle>Produtos com saldo negativo</SectionTitle>
                  <Tooltip
                    title="Produtos obtidos do estoque resumo, considerando apenas os itens com saldo negativo. Saldos ínfimos (entre -0,01 e 0) são desconsiderados."
                    arrow
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      style={{ color: "#4B5563", cursor: "help" }}
                    />
                  </Tooltip>
                </Box>
                <ButtonReportContent>
                  <Button
                    variant="outlined"
                    className={classes.reportButton}
                    fullWidth
                    onClick={handleDownloadOutOfStock}
                    isLoading={reportLoading}
                    disabled={!storageOutOfStock?.length}
                  >
                    Baixar relatório
                  </Button>
                </ButtonReportContent>
              </TablePanelHeader>

              <SearchContent>
                <SearchInput
                  placeholder="ID ou produto"
                  onChange={debaunceStorageSearch}
                />
              </SearchContent>

              <StorageTable
                loading={loadingStock}
                storageOutOfStock={storageOutOfStock}
              />
            </CustomTableContainer>
          </Content>

          <Content gridArea="batches">
            <CustomTableContainer component={Paper}>
              <TablePanelHeader>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ gap: "0.5rem" }}
                >
                  <SectionTitle>Lotes perto do vencimento</SectionTitle>
                  <CountBadge>
                    {batchData.batches?.length ?? 0} itens
                  </CountBadge>
                </Box>
                <ButtonReportContent>
                  <Button
                    variant="outlined"
                    className={classes.reportButton}
                    fullWidth
                    onClick={handleDownloadDueBatches}
                    isLoading={batchReportLoading}
                    disabled={!batchData.batches?.length}
                  >
                    Baixar relatório
                  </Button>
                </ButtonReportContent>
              </TablePanelHeader>

              <SearchContent>
                <SearchInput
                  placeholder="Lote, seller ou produto"
                  onChange={debaunceBatchSearch}
                />
              </SearchContent>

              <DashBatchTable
                reload={getBatchData}
                loading={loadingBatch}
                batchData={batchData.batches}
              />
            </CustomTableContainer>
          </Content>

          <VertDashCards
            confirmPendingData={confirmPendingData}
            loadingConfirmPending={loadingConfirmPending}
          />
        </MainContent>
      </DashboardPanel>
    </ContainerPage>
  );
};
